/* eslint-disable react/no-danger */

import { useCallback, useState, useEffect } from 'react';

import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { makeStyles } from '@mui/styles';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@classify/react-core-components/Typography';
import { LinkWrapper } from '../../../links';

const useStyles = makeStyles(
  ({ typography, spacing }) => ({
    answer: {
      '& a': {
        fontWeight: typography.fontWeightBold,
      },
      '& li': {
        ...typography.body2,
      },
    },
    videoContainer: {
      maxWidth: 320,
    },
    iframeContainer: {
      marginTop: spacing(2),
      position: 'relative',
      aspectRatio: '16/9',
      '& iframe': {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'contain',
      },
    },
    faqContainer: {
      scrollMarginTop: '60px',
    },
  }),
  { classNamePrefix: 'Faq' }
);

/* istanbul ignore next */
const getElementIdByFaqId = (faqId) => `faq_${faqId}`;
/* istanbul ignore next */
const getElementByFaqId = (faqId) => document.getElementById(getElementIdByFaqId(faqId));
/* istanbul ignore next */
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

function Faq({ children, ariaLabel = 'Show more information' }) {
  const [expandedPanelId, setExpandedPanelId] = useState(null);
  const classes = useStyles();

  const getOnPanelChangeHandler = useCallback(
    (panelId) => (e, expanded) => {
      setExpandedPanelId(expanded ? panelId : null);
    },
    [setExpandedPanelId]
  );

  const shouldOpenEntry = (id) => {
    /* istanbul ignore next */
    if (expandedPanelId === null && document.location.hash === `#${getElementIdByFaqId(id)}`) {
      return true;
    }

    return expandedPanelId === id;
  };

  /* istanbul ignore next */
  const scrollToOpenFaqItem = async (faqId) => {
    const faqElement = getElementByFaqId(faqId);

    if (faqElement) {
      setExpandedPanelId(faqId);

      await wait(150); // wait for the dom to be updated and scroll to the correct position
      faqElement.scrollIntoView({ behavior: 'smooth' });

      await wait(1000); // wait for the scroll to finish and make sure the element is focused
      faqElement.firstChild.focus();
    }
  };

  /* istanbul ignore next */
  useEffect(() => {
    const handlePageLoad = async () => {
      await wait(500); // wait a bit for the whole page to build

      const faqId = document.location.hash.replace('#', '');

      scrollToOpenFaqItem(faqId);
    };

    handlePageLoad();
  }, []);

  /* istanbul ignore next */
  useEffect(() => {
    const handleFaqLink = async (event) => {
      const { target } = event;

      if (!target?.href) {
        return;
      }

      const targetUrl = new URL(target.href);
      const faqId = targetUrl.hash.replace('#', '');

      if (targetUrl.pathname === document.location.pathname) {
        event.preventDefault();
        window.history.pushState({}, '', targetUrl);

        scrollToOpenFaqItem(faqId);
      }
    };

    document.body.addEventListener('click', handleFaqLink);

    return () => {
      document.body.removeEventListener('click', handleFaqLink);
    };
  }, []);

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: children.map((faqItem) => ({
            '@type': 'Question',
            name: faqItem.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faqItem.answer,
            },
          })),
        })}
      </script>
      {children.map((faqItem) => (
        <Accordion
          key={faqItem.id}
          id={getElementIdByFaqId(faqItem.id)}
          expanded={shouldOpenEntry(faqItem.id)}
          onChange={getOnPanelChangeHandler(faqItem.id)}
          className={classes.faqContainer}
        >
          <AccordionSummary
            aria-controls={`content-${faqItem.id}`}
            expandIcon={<ExpandMoreIcon aria-label={ariaLabel} />}
          >
            <Typography variant="body">{faqItem.question}</Typography>
          </AccordionSummary>
          <AccordionDetails tabIndex={0}>
            <LinkWrapper>
              <Typography variant="bodySmall" component="p" className={classes.answer}>
                <span dangerouslySetInnerHTML={{ __html: faqItem.answer }} />
                {faqItem.link && (
                  <>
                    {' '}
                    <a
                      href={faqItem.link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      tabIndex={expandedPanelId === faqItem.id ? 0 : -1}
                    >
                      {faqItem.link.label}
                    </a>
                  </>
                )}
                {faqItem.answerEnd}
              </Typography>
            </LinkWrapper>
            {faqItem.videoLink && (
              <div className={classes.videoContainer}>
                <div className={classes.iframeContainer}>
                  <iframe
                    title={faqItem.question}
                    src={faqItem.videoLink}
                    frameBorder="0"
                    allowFullScreen
                  />
                </div>
              </div>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  );
}

export default Faq;
